/** Datos derivados para Mi mapa. El recorrido original permanece en THERA. */
export type Periodo = "semana" | "mes" | "anio" | "todo";
export type Coordenada = [number, number] | [number, number, number];

export type Trazo = {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    properties: Record<string, string | number | boolean | null>;
    geometry: { type: "LineString"; coordinates: Coordenada[] };
  }>;
};

export type Salida = {
  id: string; // UUID de la actividad original (client_activity_id o id remoto)
  remoteId?: string;
  ownerId: string;
  routeId: string | null;
  name: string;
  kind: string;
  startedAt: number;
  finishedAt: number;
  distanceM: number;
  durationS: number;
  ascentM: number;
  pointsCount: number;
  syncStatus: "local" | "pending" | "synced" | "error";
  source: "local" | "remote";
};

export type Cifras = {
  salidas: number;
  distanceM: number;
  durationS: number;
  ascentM: number;
  activeDays: number;
  withoutTrack: number;
};

const zone = "America/Guayaquil";
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
});

export function fechaEcuador(ms: number): string {
  const parts = Object.fromEntries(dateFormatter.formatToParts(new Date(ms)).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function inicioSemana(ms: number): string {
  const [y, m, d] = fechaEcuador(ms).split("-").map(Number);
  const day = new Date(Date.UTC(y, m - 1, d));
  const daysFromMonday = (day.getUTCDay() + 6) % 7;
  day.setUTCDate(day.getUTCDate() - daysFromMonday);
  return day.toISOString().slice(0, 10);
}

export function perteneceAlPeriodo(startedAt: number, periodo: Periodo, now = Date.now()): boolean {
  if (!Number.isFinite(startedAt) || startedAt > now || startedAt <= 0) return false;
  if (periodo === "todo") return true;
  const a = fechaEcuador(startedAt);
  const b = fechaEcuador(now);
  if (periodo === "semana") return a >= inicioSemana(now) && a <= b;
  if (periodo === "mes") return a.slice(0, 7) === b.slice(0, 7);
  return a.slice(0, 4) === b.slice(0, 4);
}

export function unificarSalidas(ownerId: string, local: Salida[], remote: Salida[]): Salida[] {
  const index = new Map<string, Salida>();
  for (const item of remote) {
    if (item.ownerId === ownerId && item.finishedAt > 0) index.set(item.id, item);
  }
  for (const item of local) {
    if (item.ownerId !== ownerId || item.finishedAt <= 0) continue;
    const cloud = index.get(item.id);
    // La medición local todavía sin enviar tiene prioridad ante una copia remota antigua.
    if (!cloud || item.syncStatus !== "synced") index.set(item.id, item);
    else if (cloud && (!cloud.name || cloud.name === "Mi recorrido")) index.set(item.id, { ...cloud, name: item.name });
  }
  return [...index.values()].sort((a, b) => b.startedAt - a.startedAt || a.id.localeCompare(b.id));
}

export function salidasGuardadas(items: Salida[], savedIds: ReadonlySet<string>, periodo: Periodo, now = Date.now()) {
  return items.filter((item) => savedIds.has(item.id) && perteneceAlPeriodo(item.startedAt, periodo, now));
}

function metric(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

export function sumarSalidas(items: Salida[], hasTrack: ReadonlySet<string> = new Set()): Cifras {
  const days = new Set<string>();
  let distanceM = 0, durationS = 0, ascentM = 0;
  for (const activity of items) {
    distanceM += metric(activity.distanceM);
    durationS += metric(activity.durationS);
    ascentM += metric(activity.ascentM);
    days.add(fechaEcuador(activity.startedAt));
  }
  return {
    salidas: items.length,
    distanceM,
    durationS,
    ascentM,
    activeDays: days.size,
    withoutTrack: items.filter((item) => item.pointsCount < 2 && !hasTrack.has(item.id)).length,
  };
}

function isCoordinate(value: unknown): value is Coordenada {
  return Array.isArray(value) && value.length >= 2 &&
    value.length <= 3 && value.every((v) => typeof v === "number" && Number.isFinite(v)) &&
    value[0] >= -180 && value[0] <= 180 && value[1] >= -90 && value[1] <= 90;
}

export function extraerLinea(geojson: unknown): Coordenada[] {
  if (!geojson || typeof geojson !== "object") return [];
  const obj = geojson as { type?: string; features?: unknown[]; geometry?: unknown };
  const features = obj.type === "FeatureCollection" && Array.isArray(obj.features)
    ? obj.features : obj.type === "Feature" ? [obj] : [];
  for (const feature of features) {
    if (!feature || typeof feature !== "object") continue;
    const geometry = (feature as { geometry?: { type?: string; coordinates?: unknown } }).geometry;
    if (geometry?.type !== "LineString" || !Array.isArray(geometry.coordinates)) continue;
    const points = geometry.coordinates.filter(isCoordinate);
    // Un trazo con coordenadas inválidas no se dibuja parcial ni se presenta como medido.
    if (points.length === geometry.coordinates.length && points.length >= 2) return points;
  }
  return [];
}

/** Vista ligera para el mapa; el GPX/GeoJSON completo sigue en la actividad. */
export function simplificarLinea(points: Coordenada[], maxPoints = 180): Coordenada[] {
  if (points.length <= maxPoints) return points;
  const output: Coordenada[] = [];
  for (let i = 0; i < maxPoints; i++) output.push(points[Math.floor(i * (points.length - 1) / (maxPoints - 1))]);
  return output;
}

export function crearCapa(items: Array<{ salida: Salida; line: Coordenada[] }>, selectedId: string | null,
  allSaved: ReadonlyArray<Salida> = items.map(({ salida }) => salida)): Trazo {
  const repetition = new Map<string, number>();
  for (const salida of allSaved) if (salida.routeId) repetition.set(salida.routeId, (repetition.get(salida.routeId) ?? 0) + 1);
  return {
    type: "FeatureCollection",
    features: items.filter(({ line }) => line.length >= 2).map(({ salida, line }) => ({
      type: "Feature" as const,
      properties: {
        activity_id: salida.id,
        selected: salida.id === selectedId,
        intensity: Math.min(5, repetition.get(salida.routeId ?? "") ?? 1),
      },
      geometry: { type: "LineString" as const, coordinates: simplificarLinea(line) },
    })),
  };
}

export function formatDuration(seconds: number): string {
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const totalMinutes = Math.round(safeSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
}
